import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  ArrowLeft,
  ChevronDown,
} from 'lucide-react';
import ForceGraph from './ForceGraph';
import ForceGraph3D from './ForceGraph3D';
import MindMapGraph from './MindMapGraph';
import GlobeGraph from './GlobeGraph';
import EntityDetails from './EntityDetails';

const ENTITY_TYPES = ['SERVICE', 'TEAM', 'PATTERN', 'API', 'REGULATION', 'DOCUMENT', 'CAPABILITY'];
const RELATIONSHIP_TYPES = ['DEPENDS_ON', 'OWNED_BY', 'USES_PATTERN', 'COMPLIES_WITH', 'INTEGRATES_WITH', 'DOCUMENTS', 'MENTIONS'];

const VIEW_MODES = [
  { id: 'mindmap', name: 'Mind Map', description: 'Radial hierarchy layout', is3D: false },
  { id: 'force', name: 'Force Graph', description: 'Physics-based 2D layout', is3D: false },
  { id: 'globe', name: 'Globe', description: 'Nodes on 3D sphere', is3D: true },
  { id: 'force3d', name: '3D Force', description: 'Physics-based 3D layout', is3D: true },
];

function ExploreGraph({ entities, relationships, loading }) {
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [relTypeFilter, setRelTypeFilter] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('force3d'); // Default to 3D Force
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [drawerWidth, setDrawerWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  const currentView = VIEW_MODES.find(v => v.id === viewMode) || VIEW_MODES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowViewDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const detailsWidth = selectedEntity ? drawerWidth : 0;
        setDimensions({
          width: Math.max(400, rect.width - detailsWidth),
          height: Math.max(400, rect.height),
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [selectedEntity, drawerWidth]);

  // Handle drawer resize
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const maxWidth = containerRect.width * 0.5;
      const minWidth = 280;

      const newWidth = containerRect.right - e.clientX;
      setDrawerWidth(Math.min(maxWidth, Math.max(minWidth, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Filter entities
  const filteredEntities = entities.filter(entity => {
    const matchesSearch = !searchQuery ||
      entity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entity.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = typeFilter.length === 0 || typeFilter.includes(entity.entity_type);

    return matchesSearch && matchesType;
  });

  // Filter relationships
  const filteredRelationships = relationships.filter(rel => {
    const sourceIncluded = filteredEntities.some(e => e.id === rel.source_id);
    const targetIncluded = filteredEntities.some(e => e.id === rel.target_id);
    const matchesRelType = relTypeFilter.length === 0 || relTypeFilter.includes(rel.rel_type);

    return sourceIncluded && targetIncluded && matchesRelType;
  });

  const selectedEntityData = entities.find(e => e.id === selectedEntity);
  const selectedRelationships = {
    outgoing: relationships.filter(r => r.source_id === selectedEntity),
    incoming: relationships.filter(r => r.target_id === selectedEntity),
  };

  const toggleTypeFilter = (type) => {
    setTypeFilter(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleRelTypeFilter = (type) => {
    setRelTypeFilter(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const clearFilters = () => {
    setTypeFilter([]);
    setRelTypeFilter([]);
    setSearchQuery('');
  };

  const renderGraph = () => {
    const props = {
      entities: filteredEntities,
      relationships: filteredRelationships,
      selectedEntity,
      onSelectEntity: setSelectedEntity,
      width: dimensions.width,
      height: dimensions.height,
    };

    switch (viewMode) {
      case 'mindmap':
        return <MindMapGraph {...props} />;
      case 'force':
        return <ForceGraph {...props} />;
      case 'globe':
        return <GlobeGraph {...props} />;
      case 'force3d':
        return <ForceGraph3D {...props} />;
      default:
        return <GlobeGraph {...props} />;
    }
  };

  const getInstructions = () => {
    switch (viewMode) {
      case 'mindmap':
        return 'Scroll to zoom • Drag to pan • Click node to select';
      case 'force':
        return 'Scroll to zoom • Drag nodes to reposition • Click to select';
      case 'globe':
        return 'Drag to rotate globe • Scroll to zoom • Click node to select';
      case 'force3d':
        return 'Drag to rotate • Scroll to zoom • Click node to select';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-dark-300">Loading graph...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-dark-600 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selectedEntity && (
            <button
              onClick={() => setSelectedEntity(null)}
              className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <h1 className="text-xl font-semibold">Explore Graph</h1>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400" />
            <input
              type="text"
              placeholder="Search entities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-dark-700 border border-dark-600 rounded-lg pl-10 pr-4 py-2 text-sm w-64 focus:outline-none focus:border-accent-purple transition-colors"
            />
          </div>

          {/* Filter Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-colors ${
              showFilters || typeFilter.length > 0 || relTypeFilter.length > 0
                ? 'bg-accent-purple text-white'
                : 'hover:bg-dark-700'
            }`}
          >
            <Filter size={18} />
          </button>

          {/* View Mode Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowViewDropdown(!showViewDropdown)}
              className="flex items-center gap-2 px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg hover:bg-dark-600 transition-colors"
            >
              <span className={`w-2 h-2 rounded-full ${currentView.is3D ? 'bg-accent-purple' : 'bg-accent-blue'}`} />
              <span className="text-sm font-medium">{currentView.name}</span>
              <ChevronDown size={14} className={`transition-transform ${showViewDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showViewDropdown && (
              <div className="absolute right-0 mt-2 w-56 bg-dark-800 border border-dark-600 rounded-lg shadow-xl z-50 overflow-hidden">
                <div className="p-2">
                  <div className="text-xs text-dark-400 px-2 py-1 mb-1">2D Views</div>
                  {VIEW_MODES.filter(v => !v.is3D).map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => {
                        setViewMode(mode.id);
                        setShowViewDropdown(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                        viewMode === mode.id
                          ? 'bg-accent-purple/20 text-accent-purple'
                          : 'hover:bg-dark-700'
                      }`}
                    >
                      <span className="w-2 h-2 rounded-full bg-accent-blue" />
                      <div>
                        <div className="text-sm font-medium">{mode.name}</div>
                        <div className="text-xs text-dark-400">{mode.description}</div>
                      </div>
                    </button>
                  ))}

                  <div className="text-xs text-dark-400 px-2 py-1 mt-2 mb-1">3D Views</div>
                  {VIEW_MODES.filter(v => v.is3D).map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => {
                        setViewMode(mode.id);
                        setShowViewDropdown(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                        viewMode === mode.id
                          ? 'bg-accent-purple/20 text-accent-purple'
                          : 'hover:bg-dark-700'
                      }`}
                    >
                      <span className="w-2 h-2 rounded-full bg-accent-purple" />
                      <div>
                        <div className="text-sm font-medium">{mode.name}</div>
                        <div className="text-xs text-dark-400">{mode.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="p-4 border-b border-dark-600 bg-dark-800">
          <div className="flex items-start gap-6">
            {/* Entity Type Filters */}
            <div>
              <h3 className="text-sm font-semibold text-dark-300 mb-2">Entity Types</h3>
              <div className="flex flex-wrap gap-2">
                {ENTITY_TYPES.map(type => (
                  <button
                    key={type}
                    onClick={() => toggleTypeFilter(type)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      typeFilter.includes(type)
                        ? 'bg-accent-purple text-white'
                        : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Relationship Type Filters */}
            <div>
              <h3 className="text-sm font-semibold text-dark-300 mb-2">Relationship Types</h3>
              <div className="flex flex-wrap gap-2">
                {RELATIONSHIP_TYPES.map(type => (
                  <button
                    key={type}
                    onClick={() => toggleRelTypeFilter(type)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      relTypeFilter.includes(type)
                        ? 'bg-accent-purple text-white'
                        : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Button */}
            {(typeFilter.length > 0 || relTypeFilter.length > 0 || searchQuery) && (
              <button
                onClick={clearFilters}
                className="text-sm text-accent-purple hover:text-accent-purple-light transition-colors"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="mt-3 text-xs text-dark-400">
            Showing {filteredEntities.length} entities and {filteredRelationships.length} relationships
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" ref={containerRef}>
        {/* Graph */}
        <div className="flex-1 relative">
          {renderGraph()}

          {/* Legend */}
          <div className="absolute bottom-4 left-4 bg-dark-800/90 backdrop-blur-sm rounded-lg p-3 border border-dark-600">
            <h4 className="text-xs font-semibold text-dark-300 mb-2">Legend</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-dark-300">Service</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-dark-300">Team</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-dark-300">Pattern</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-dark-300">API</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-dark-300">Regulation</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                <span className="text-dark-300">Document</span>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="absolute top-4 left-4 text-xs text-dark-400">
            {getInstructions()}
          </div>
        </div>

        {/* Entity Details Panel */}
        {selectedEntity && (
          <div className="flex-shrink-0 relative" style={{ width: drawerWidth }}>
            {/* Resize Handle */}
            <div
              onMouseDown={handleMouseDown}
              className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-accent-purple/50 transition-colors z-10 ${
                isResizing ? 'bg-accent-purple' : 'bg-transparent'
              }`}
              title="Drag to resize"
            />
            <EntityDetails
              entity={selectedEntityData}
              relationships={selectedRelationships}
              onClose={() => setSelectedEntity(null)}
              onSelectEntity={setSelectedEntity}
              allEntities={entities}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ExploreGraph;
